import React, { useState, useEffect, useRef } from "react";
import {
    Table,
    Input,
    Button,
    Space,
    Spin,
} from "antd";
import CommonObj from "../../CommonObj";
import { getUTCDate } from "../../components/utcDate";
import {
    CloseCircleOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import ExternalApiSliderUI from "./externalApi-slide";
import EditIcon from "../../assets/img/svg/xgridediticon.svg";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { renderCommonPaginationTotal } from "../../components/commonpagination/common-pagination";
import "../../components/commonpagination/common-pagination.css";

const ExternalApiMaster = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalRow, setTotalRow] = useState(1);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchFilters, setsearchFilters] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [startTime, setStartTime] = useState("");
    const { accessToken } = useAuthToken();
    const [visible, setVisible] = useState(false);
    const [selectedApiName, setSelectedApiName] = useState("");

    const searchInput = useRef(null);

    const handleReset = (dataIndex, clearFilters) => {
        clearFilters();
        setsearchFilters((prev) => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        fetchData({
            pagination,
            sorter: sortedInfo,
            filters: { ...searchFilters, [dataIndex]: undefined },
        });
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
        }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    style={{ marginBottom: 8, display: "block" }}
                    onPressEnter={() => confirm()}
                />
                <Space>
                    <Button type="primary" size="small" onClick={() => confirm()}>
                        Search
                    </Button>
                    <Button
                        size="small"
                        onClick={() => {
                            handleReset(dataIndex, clearFilters);
                            confirm();
                        }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
        ),
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
        onFilter: (value, record) =>
            record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current.select(), 100);
            }
        },
        sorter: true,
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null,
    });

    const showUserModal = () => {
        setVisible(true);
    };

    const columns = [
        {
            title: "API URL",
            dataIndex: "apiurl",
            key: "apiurl",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "apiurl" ? sortedInfo.order : null,
            ...getColumnSearchProps("apiurl"),
        },
        {
            title: "API Key",
            dataIndex: "apiKey",
            key: "apiKey",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "apiKey" ? sortedInfo.order : null,
            ...getColumnSearchProps("apiKey"),
        },
        {
            title: "App Name",
            dataIndex: "appName",
            key: "appName",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "appName" ? sortedInfo.order : null,
            ...getColumnSearchProps("appName"),
        },
        {
            title: "Sort Order",
            dataIndex: "sortOrder",
            key: "sortOrder",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "sortOrder" ? sortedInfo.order : null,
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "isActive",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "isActive" ? sortedInfo.order : null,
            render: (_, record) => (
                <Space size="middle">
                    {record.isActive === 1 && (
                        <div className="grid-badge accepted">
                            <span>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_3194_20866)">
                                        <circle
                                            cx="7.9987"
                                            cy="8.00065"
                                            r="6.66667"
                                            stroke="#229100"
                                            strokeWidth="1.2"
                                        />
                                        <path
                                            d="M5.66797 8.33398L7.0013 9.66732L10.3346 6.33398"
                                            stroke="#229100"
                                            strokeWidth="1.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_3194_20866">
                                            <rect width="16" height="16" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>Active
                        </div>
                    )}
                    {record.isActive === 0 && (
                        <div className="grid-badge rejected">
                            <CloseCircleOutlined />
                            <span>InActive</span>
                        </div>
                    )}
                </Space>
            ),
        },
        {
            title: "Created By",
            dataIndex: "createdBy",
            key: "createdBy",
            sorter: true,
            sortOrder: sortedInfo.columnKey === "createdBy" ? sortedInfo.order : null,
        },
        {
            title: "Actions",
            key: "actions",
            width: 100,
            align: "center",
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="text"
                        icon={<img src={EditIcon} />}
                        onClick={() => handleEdit(record)}
                    />
                </Space>
            ),
        },
    ];

    const getRowClassName = (index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });

        setsearchFilters(normalizeFilters);
        setSortedInfo(sorter);

        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        fetchData({
            pagination: {
                current: newPagination.current,
                pageSize: newPagination.pageSize,
            },
            sorter,
            filters: normalizeFilters,
        });
    };

    const handleEdit = (key) => {
        setSelectedApiName(key.appName);
        setSelectedRecord({ ...key });
        showUserModal();
    };

    const handleAddNew = () => {
        setSelectedRecord(null);
        setVisible(true);
    };

    const fetchData = async ({ pagination: pag, filters, sorter }) => {
        const requestData = {
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            search: JSON.stringify(filters),
            orderByColumn: sorter?.field || "CreatedOn",
            orderBy: sorter?.order === "ascend" ? "ASC" : "DESC",
        };

        setLoading(true);
        try {
            const myHeaders = {
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };

            let formData = new FormData();
            Object.keys(requestData).forEach(function (key) {
                formData.append(key, requestData[key]);
            });

            const response = await fetch(CommonObj.Endpoints.GetExternalAPIList, {
                method: "POST",
                headers: myHeaders,
                body: formData,
            });

            if (!response.ok) throw new Error("Network response was not ok");

            const responseText = await response.text();
            let result;

            try {
                if (responseText.includes("No records found")) {
                    result = [];
                } else {
                    result = JSON.parse(responseText);
                }
            } catch (e) {
                if (responseText.includes("No records found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || "Failed to parse API response.");
                }
            }

            const records = Array.isArray(result) ? result : [result];
            const formattedData = records.map((item) => ({ ...item, key: item.externalAPIID }));

            setData(formattedData);

            const total = formattedData[0]?.totalRows || 0;
            setTotalRow(total);
            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));

            const startUTCTime = getUTCDate();
            setStartTime(startUTCTime?.UTCDate);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });
    }, []);

    if (loading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }

    return (
        <div className="active-page-container">
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>External API Master</h4>
                </div>
                <div className="active-page-header-action-container">
                    <div className="active-page-header-button-container">
                    </div>
                    <div className="active-page-header-button-container">
                        <button
                            className="active-page-header-add-button"
                            onClick={handleAddNew}
                        >
                            <span>
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_1193_9975)">
                                        <circle
                                            opacity="0.5"
                                            cx="8.00016"
                                            cy="7.99992"
                                            r="6.66667"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                        />
                                        <path
                                            d="M10 8.00002L8 8.00002M8 8.00002L6 8.00002M8 8.00002L8 6M8 8.00002L8 10"
                                            stroke="currentColor"
                                            strokeWidth="1.2"
                                            strokeLinecap="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1193_9975">
                                            <rect width="16" height="16" fill="currentColor" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </span>Add New
                        </button>
                    </div>
                </div>
            </div>
            <div className="active-page-body-container">
                <Table
                    columns={columns}
                    dataSource={data}
                    className="common-pagination common-grid user-management-table"
                    pagination={data.length > 0 ? {
                        ...pagination,
                        pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                        showSizeChanger: true,
                        position: ["bottomRight"],
                        defaultPageSize: 10,
                        showTotal: (total, range) =>
                            renderCommonPaginationTotal(total, range, {
                                pagination,
                                handleTableChange,
                                searchFilters,
                                sortedInfo,
                            }),
                    } : false}
                    rowKey={(record) => record.externalAPIID}
                    onChange={handleTableChange}
                    scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
                    rowClassName={getRowClassName}
                />

                <ExternalApiSliderUI
                    visible={visible}
                    selectedApiName={selectedApiName}
                    setSelectedApiName={setSelectedApiName}
                    width={596}
                    title="Add External API Master"
                    onClose={() => setVisible(false)}
                    selectedRecord={selectedRecord}
                    className="story-slider adduser-slider"
                    onSuccess={() => {
                        fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });
                        setSelectedRecord(null);
                    }}
                />
            </div>
        </div>
    );
};

export default ExternalApiMaster;
